
import { cn } from "@/lib/utils";
import {
  Box,
  ChevronLeft,
  ChevronRight,
  Leaf,
  Package,
  Settings2,
  X,
} from "lucide-react";
import { type ReactNode } from "react";
import { useEditMode } from "../hooks/useEditMode";
import { useSidePanel } from "../hooks/useSidePanel";
import { useSidePanelWidth } from "../hooks/useSidePanelWidth";
import { useIsMobile } from "@/lib/useMediaQuery";
import { SpacePanel } from "./SpacePanel";
import { RenderingDebugPanel } from "./RenderingDebugPanel";
import { ModelsTab } from "./ModelsTab";
import { ObjectsTab } from "./ObjectsTab";

// ─── Tab button ─────────────────────────────────────────────

function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: ReactNode;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex-1 flex items-center justify-center gap-1.5 px-2 md:px-3 py-2.5 text-xs font-medium uppercase tracking-wider transition-colors min-h-[44px]",
        active
          ? "text-sands-800 border-b-2 border-healing-500"
          : "text-sands-500 hover:text-sands-700",
      )}
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

// ─── Tab bar content (shared between desktop panel and mobile overlay) ───

function TabBar() {
  const { sidePanelTab, setSidePanelTab } = useSidePanel();

  return (
    <div className="flex border-b border-sands-200 shrink-0">
      <TabButton
        active={sidePanelTab === "models"}
        onClick={() => setSidePanelTab("models")}
        icon={<Package className="w-3.5 h-3.5" />}
        label="Models"
      />
      <TabButton
        active={sidePanelTab === "object"}
        onClick={() => setSidePanelTab("object")}
        icon={<Box className="w-3.5 h-3.5" />}
        label="Objects"
      />
      <TabButton
        active={sidePanelTab === "space"}
        onClick={() => setSidePanelTab("space")}
        icon={<Leaf className="w-3.5 h-3.5" />}
        label="Space"
      />
      {import.meta.env.DEV && (
        <TabButton
          active={sidePanelTab === "debug"}
          onClick={() => setSidePanelTab("debug")}
          icon={<Settings2 className="w-3.5 h-3.5" />}
          label="Debug"
        />
      )}
    </div>
  );
}

function TabContent() {
  const { sidePanelTab } = useSidePanel();

  return (
    <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
      {sidePanelTab === "models" && <ModelsTab />}
      {sidePanelTab === "object" && <ObjectsTab />}
      {sidePanelTab === "space" && <SpacePanel />}
      {sidePanelTab === "debug" && <RenderingDebugPanel />}
    </div>
  );
}

// ─── SidePanel (main export) ────────────────────────────────

/**
 * Unified right-side panel for Models, Object Inspector, and Environment Settings.
 *
 * Desktop behavior:
 * - Sliding panel (resizable 280–560px) with collapse/expand toggle
 * - Hidden completely in view mode (readonly)
 *
 * Mobile behavior:
 * - Full-screen overlay modal with close button
 * - Triggered by MobileFAB (not shown here)
 */
export function SidePanel() {
  const { sidePanelOpen, toggleSidePanel } = useSidePanel();
  const { isReadonly } = useEditMode();
  const isMobile = useIsMobile();
  const { width, isDragging, onResizePointerDown } = useSidePanelWidth();

  // Side panel is not available in view mode
  if (isReadonly) return null;

  // ── Mobile: Full-screen overlay ───────────────────────────
  if (isMobile) {
    if (!sidePanelOpen) return null;

    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-white/95 backdrop-blur-sm animate-fade-in">
        {/* Header: close button */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-sands-200 shrink-0">
          <h2 className="text-sm font-semibold text-sands-800">Editor Panel</h2>
          <button
            onClick={toggleSidePanel}
            className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-sands-100 transition-colors"
            title="Close panel"
          >
            <X className="w-5 h-5 text-sands-600" />
          </button>
        </div>

        {/* Tab bar */}
        <TabBar />

        {/* Tab content */}
        <TabContent />
      </div>
    );
  }

  // ── Desktop: Sliding side panel ───────────────────────────

  // In edit mode, show a floating toggle button when panel is closed
  if (!sidePanelOpen) {
    return (
      <button
        onClick={toggleSidePanel}
        className="w-8 h-16 flex items-center justify-center rounded-l-lg bg-white/90 backdrop-blur-sm border border-r-0 border-sands-200 shadow-sm hover:bg-sands-50 transition-colors z-30"
        title="Open side panel"
        style={{
          position: "fixed",
          right: 0,
          top: "50%",
          transform: "translateY(-50%)",
        }}
      >
        <ChevronLeft className="w-4 h-4 text-sands-500" />
      </button>
    );
  }

  return (
    <div
      className="relative flex-shrink-0 z-20 h-full"
      style={{ width: sidePanelOpen ? width : 0 }}
    >
      {/* Collapse/expand toggle button (always visible) */}
      <button
        onClick={toggleSidePanel}
        className={cn(
          "absolute top-1/2 -translate-y-1/2 -left-4 w-8 h-16 flex items-center justify-center rounded-l-lg bg-white/90 backdrop-blur-sm border border-r-0 border-sands-200 shadow-sm hover:bg-sands-50 transition-colors z-30",
          !sidePanelOpen && "left-auto -left-8",
        )}
        title={sidePanelOpen ? "Collapse panel" : "Expand panel"}
      >
        {sidePanelOpen ? (
          <ChevronRight className="w-4 h-4 text-sands-500" />
        ) : (
          <ChevronLeft className="w-4 h-4 text-sands-500" />
        )}
      </button>

      {/* Drag handle along the left edge — desktop resize */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize side panel"
        onPointerDown={onResizePointerDown}
        className="absolute inset-y-0 left-0 z-20 w-1.5 cursor-ew-resize hover:bg-healing-300/80 active:bg-healing-400/80"
      />

      {/* Panel content */}
      <div
        className={cn(
          "h-full bg-white/95 backdrop-blur-sm border-l border-sands-200 shadow-lg flex flex-col overflow-hidden",
          !isDragging && "transition-all duration-200",
          sidePanelOpen ? "opacity-100" : "opacity-0 pointer-events-none",
        )}
        style={{ width }}
      >
        {/* Tab bar */}
        <TabBar />

        {/* Tab content */}
        <TabContent />
      </div>
    </div>
  );
}
